/**
 * Ported from QR2 (apps/scan/decoder.ts). QR2 read QR codes only; the warehouse also needs the
 * linear codes on location labels (Code 128 from the label printer, Code 39 on the PDF labels and
 * documents) and product EANs. Native BarcodeDetector where it exists (Chrome on Android), the
 * zxing-wasm ponyfill everywhere else (Safari on iPhone), with the wasm bundled, not fetched from a CDN.
 */
import { BarcodeDetector as PonyDetector, setZXingModuleOverrides } from 'barcode-detector/ponyfill';
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

export type DecodeSource = HTMLVideoElement | HTMLCanvasElement | ImageBitmap;

export interface Decoder {
  kind: 'native' | 'wasm';
  detect(src: DecodeSource): Promise<string | null>;
}

const FORMATS = ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'data_matrix', 'itf'] as const;

let overridden = false;

type Detected = { rawValue?: string }[];
type DetectorLike = { detect(src: unknown): Promise<Detected> };

function wrap(kind: Decoder['kind'], det: DetectorLike): Decoder {
  return {
    kind,
    async detect(src) {
      const res = await det.detect(src);
      const hit = res?.find((r) => typeof r.rawValue === 'string' && r.rawValue.length > 0);
      return hit ? hit.rawValue!.trim() : null;
    },
  };
}

export async function createDecoder(): Promise<Decoder> {
  const Native = (window as unknown as { BarcodeDetector?: { new (o: { formats: string[] }): DetectorLike; getSupportedFormats?: () => Promise<string[]> } }).BarcodeDetector;
  if (Native) {
    try {
      const supported = (await Native.getSupportedFormats?.()) ?? [...FORMATS];
      const formats = FORMATS.filter((f) => supported.includes(f));
      if (formats.includes('code_128') && formats.includes('qr_code')) {
        const det = new Native({ formats: [...formats] });
        // Probe once so a broken implementation falls through to the wasm decoder.
        const probe = document.createElement('canvas');
        probe.width = 8;
        probe.height = 8;
        await det.detect(probe);
        return wrap('native', det);
      }
    } catch {
      // fall through
    }
  }
  if (!overridden) {
    setZXingModuleOverrides({ locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) });
    overridden = true;
  }
  const det = new PonyDetector({ formats: [...FORMATS] });
  return wrap('wasm', det as unknown as DetectorLike);
}

/** Decodes a photo (fallback when the live camera is unusable). */
export async function decodeImageFile(decoder: Decoder, file: File): Promise<string | null> {
  const bitmap = await createImageBitmap(file);
  try {
    return await decoder.detect(bitmap);
  } finally {
    bitmap.close?.();
  }
}
